import { Component, OnInit } from '@angular/core';
import { environment } from 'src/environments/environment';
import { BulevartService } from './bulevart.service';
import { helper } from 'src/environments/helper';

@Component({
    selector: 'assets-bulevart',
    templateUrl: 'bulevart.component.html',
    styleUrls: ['bulevart.component.scss']
})
export class BulevartComponent implements OnInit {
    portalLive: boolean = environment.portalLive;
    basePath: string = environment.interop.authCore.basePath;

    currentIndex = 0;
    interval: any;

    // Use API response structure directly
    categories: any[] = [];
    isLoading: boolean = true;
    error: string | null = null;

    // Modal properties
    showModal: boolean = false;
    selectedImage: any = null;
    currentGroupImages: any[] = [];
    currentImageIndex: number = 0;
    currentGroupTitle: string = '';

    // Load more functionality
    imageVisibilityMap: Map<string, number> = new Map(); // Now stores number of visible images

    helper = helper;

    constructor(
        private bulevartService: BulevartService
    ) { }

    ngOnInit() {
        this.loadCategoriesData();
    }

    loadCategoriesData() {
        this.isLoading = true;
        this.error = null;

        this.bulevartService.listGallery().subscribe({
            next: (response) => {
                if (response.errors === false && response.data) {
                    this.categories = response.data;
                    this.initializeImageVisibility();
                    this.isLoading = false;
                } else {
                    this.error = 'Failed to load data';
                    this.isLoading = false;
                }
            },
            error: (err) => {
                this.error = 'Error loading data: ' + err.message;
                this.isLoading = false;
                console.error('Error loading categories data:', err);
            }
        });
    }

    // Initialize visibility state for all image groups
    initializeImageVisibility() {
        this.categories.forEach(category => {
            category.subcategories.forEach((subcategory: any) => {
                if (subcategory.images && subcategory.images.length > 0) {
                    const subcategoryId = this.getGroupId(category.title, subcategory.title);
                    this.imageVisibilityMap.set(subcategoryId, 10); // Start with 10 images visible
                }

                if (subcategory.tertiary_categories) {
                    subcategory.tertiary_categories.forEach((tertiary: any) => {
                        if (tertiary.images && tertiary.images.length > 0) {
                            const tertiaryId = this.getGroupId(category.title, subcategory.title, tertiary.title);
                            this.imageVisibilityMap.set(tertiaryId, 10); // Start with 10 images visible
                        }
                    });
                }
            });
        });
    }

    // Generate unique ID for each group
    getGroupId(categoryTitle: string, subcategoryTitle: string, tertiaryTitle?: string): string {
        if (tertiaryTitle) {
            return `${categoryTitle}-${subcategoryTitle}-${tertiaryTitle}`;
        }
        return `${categoryTitle}-${subcategoryTitle}`;
    }

    // Find which group contains the selected image
    findImageGroup(selectedImage: any): { images: any[], title: string } {
        for (const category of this.categories) {
            for (const subcategory of category.subcategories) {
                // Check subcategory images
                if (subcategory.images && subcategory.images.includes(selectedImage)) {
                    return {
                        images: subcategory.images,
                        title: subcategory.title
                    };
                }

                // Check tertiary category images
                if (subcategory.tertiary_categories) {
                    for (const tertiary of subcategory.tertiary_categories) {
                        if (tertiary.images && tertiary.images.includes(selectedImage)) {
                            return {
                                images: tertiary.images,
                                title: tertiary.title
                            };
                        }
                    }
                }
            }
        }

        // Fallback - return single image if not found in any group
        return {
            images: [selectedImage],
            title: selectedImage.original_filename || 'Unknown'
        };
    }

    getImageUrl(image: any, size : string = 'full_display'): string {
        return this.basePath + image[size];
        // return this.basePath + image.full_display; // backward compatibility
    }

    // Modal methods
    openModal(image: any) {
        this.selectedImage = image;

        // Find the group this image belongs to
        const groupInfo = this.findImageGroup(image);
        this.currentGroupImages = groupInfo.images;
        this.currentGroupTitle = groupInfo.title;

        // Find the index of this image within its group
        this.currentImageIndex = this.currentGroupImages.findIndex(img => img === image);

        this.showModal = true;
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
    }

    closeModal() {
        this.showModal = false;
        this.selectedImage = null;
        this.currentGroupImages = [];
        this.currentImageIndex = 0;
        this.currentGroupTitle = '';
        // Restore body scroll
        document.body.style.overflow = 'auto';
    }

    // Navigation methods
    nextImage() {
        if (this.currentImageIndex < this.currentGroupImages.length - 1) {
            this.currentImageIndex++;
            this.selectedImage = this.currentGroupImages[this.currentImageIndex];
        }
    }

    previousImage() {
        if (this.currentImageIndex > 0) {
            this.currentImageIndex--;
            this.selectedImage = this.currentGroupImages[this.currentImageIndex];
        }
    }

    // Check if navigation is possible within the current group
    hasNextImage(): boolean {
        return this.currentImageIndex < this.currentGroupImages.length - 1;
    }

    hasPreviousImage(): boolean {
        return this.currentImageIndex > 0;
    }

    // Close modal when clicking outside the image
    onModalBackdropClick(event: Event) {
        if (event.target === event.currentTarget) {
            this.closeModal();
        }
    }

    // Handle escape key to close modal and arrow keys for navigation
    onKeyDown(event: KeyboardEvent) {
        if (!this.showModal) return;

        switch (event.key) {
            case 'Escape':
                this.closeModal();
                break;
            case 'ArrowLeft':
                event.preventDefault();
                this.previousImage();
                break;
            case 'ArrowRight':
                event.preventDefault();
                this.nextImage();
                break;
        }
    }

    // Load more functionality
    getVisibleImages(images: any[], groupId: string): any[] {
        const visibleCount = this.imageVisibilityMap.get(groupId) || 10;
        return images.slice(0, visibleCount);
    }

    shouldShowLoadMoreButton(images: any[], groupId: string): boolean {
        const visibleCount = this.imageVisibilityMap.get(groupId) || 10;
        return visibleCount < images.length;
    }

    shouldShowShowLessButton(images: any[], groupId: string): boolean {
        const visibleCount = this.imageVisibilityMap.get(groupId) || 10;
        return visibleCount > 10;
    }

    loadMoreImages(groupId: string, totalImages: number) {
        const currentVisible = this.imageVisibilityMap.get(groupId) || 10;
        const newVisible = Math.min(currentVisible + 10, totalImages);
        this.imageVisibilityMap.set(groupId, newVisible);
    }

    showLessImages(groupId: string) {
        const currentVisible = this.imageVisibilityMap.get(groupId) || 10;
        const newVisible = Math.max(currentVisible - 10, 10);
        this.imageVisibilityMap.set(groupId, newVisible);
    }

    getRemainingImagesCount(images: any[], groupId: string): number {
        const visibleCount = this.imageVisibilityMap.get(groupId) || 10;
        const remaining = images.length - visibleCount;
        return Math.min(remaining, 10);
    }

    getVisibleImagesCount(groupId: string): number {
        return this.imageVisibilityMap.get(groupId) || 10;
    }

    getSubcategoryId(categoryTitle: string, subcategoryTitle: string): string {
        return this.getGroupId(categoryTitle, subcategoryTitle);
    }

    getTertiaryId(categoryTitle: string, subcategoryTitle: string, tertiaryTitle: string): string {
        return this.getGroupId(categoryTitle, subcategoryTitle, tertiaryTitle);
    }
}